import { useContext, useEffect, useState } from "react";
import {
  Typography,
  Grid,
  CircularProgress,
  Box,
  Button,
} from "@mui/material";
import { MobileStateContext } from "../../../contexts/MobileContext";
import GenericSelect from "../../shared/GenericSelect";
import SearchBar from "../../shared/SearchBar";
import DateInput from "../../shared/DateInput";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import StyledIconButton from "../../shared/StyledIconButton";
import OrderCard from "../OrderCard";

import { HeadCell, PaginationInfo, JourReports } from "../../../utils/types";

interface InputProps {
  reportCount: number;
  page: number;
  setPage: (p: number) => void;
  pageSize: number;
  setPageSize: (s: number) => void;
  orders: JourReports[];
  signalFetchJourReports: () => void;
  loading: boolean;
  filterDate: Date | null;
  setFilterDate: (d: Date | null) => void;
  filterCompany: string | null;
  setFilterCompany: (c: string | null) => void;
  filterText: string | null;
  setFilterText: (t: string | null) => void;
  fetchedArchived: boolean;
  companyNames: string[];
  setColumnToSort: (s: string) => void;
  setSortDirection: (d: "asc" | "desc") => void;
}

export default function OrderTableMobile(props: InputProps) {
  const { isMobile, isIpad, isDesktop } = useContext(MobileStateContext);

  const start = props.page * props.pageSize + 1;
  const endb = (props.page + 1) * props.pageSize;
  const end = endb < props.reportCount ? endb : props.reportCount;

  const [sortField, setSortField] = useState("custom_id");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    props.setColumnToSort(sortField);
  }, [sortField]);

  useEffect(() => {
    props.setSortDirection(sortDirection);
  }, [sortDirection]);

  return (
    <Grid container display="flex" justifyContent="center" flexDirection="column">
      {/* Filters and search */}
      <Grid>
        <Grid
          item
          xs={isDesktop ? 6 : 12}
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={isMobile ? { width: "100%" } : { width: "80%" }}
        >
          <Grid item xs={6}>
            <GenericSelect
              selectData={props.filterCompany}
              setSelectData={props.setFilterCompany}
              label="Välj bolag"
              data={props.companyNames}
            />
          </Grid>
          <Grid sx={{ p: 1 }} />
          <Grid item xs={6}>
            <DateInput
              newDate={props.filterDate}
              setNewDate={props.setFilterDate}
              showClearField={true}
            />
          </Grid>
        </Grid>

        {!isDesktop && <Grid sx={{ p: 1 }} item xs={12} />}

        <Grid
          item
          xs={isDesktop ? 8 : 12}
          sx={isMobile ? { width: "100%" } : { width: "80%", m: "0 15px 0 0" }}
        >
          <SearchBar
            searchValue={props.filterText}
            setSearchValue={props.setFilterText}
            placeholder="Sök uppdrag"
          />
        </Grid>

        {/* Sorting */}
        <Grid item xs={12} sx={(isIpad || isMobile) ? { width: "100%" } : { width: "80%", m: "0 15px 0 0" }}>

          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            gap={0}
            mt={1}
            sx={{ width: "100%" }}
          >
            <Typography  justifyContent="flex-start" variant="body2" color="textSecondary">
              Sorteringsordning:
            </Typography>
            <StyledIconButton
              icon={<ArrowUpwardIcon />}
              onClick={() => setSortDirection("asc")}
              disabled={sortDirection === "asc"}
            />
            <StyledIconButton
              icon={<ArrowDownwardIcon />}
              onClick={() => setSortDirection("desc")}
              disabled={sortDirection === "desc"}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Report Cards */}
      {props.loading ? (
        <Box width={"100%"} display="flex" justifyContent={"center"} py={10}>
          <CircularProgress />
        </Box>
      ) : props.orders.length > 0 ? (
        props.orders.map((order, index) => (
          <Box component="span" sx={{ p: 2 }} key={index}>
            <OrderCard
              report={order}
              signalFetchJourReports={props.signalFetchJourReports}
              fetchedArchive={props.fetchedArchived}
            />
          </Box>
        ))
      ) : (
        <Typography sx={{ m: "20px" }}>Inga uppdrag hittades</Typography>
      )}

      {/* Pagination */}
      <Grid
        sx={{
          position: "fixed",
          width: "100vw",
          bottom: 0,
          left: 0,
          backgroundColor: "#EFEFEF",
        }}
      >
        {props.orders.length > 0 && (
          <Grid container display="flex" alignItems="center">
            <Grid item xs={isDesktop ? 8 : 6}>
              <Typography sx={{ color: "gray", textAlign: "center" }}>
                Visar {start} - {end} av {props.reportCount} uppdrag
              </Typography>
            </Grid>
            <Grid item xs={isDesktop ? 4 : 6}>
              <Grid
                container
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography sx={{ textAlign: "center", color: "gray" }}>
                  Du är på sida <b>{props.page + 1}</b>
                </Typography>
                <Grid>
                  <StyledIconButton
                    icon={<ArrowLeftIcon />}
                    disabled={props.page === 0}
                    onClick={() => props.setPage(props.page - 1)}
                  />
                  <StyledIconButton
                    icon={<ArrowRightIcon />}
                    disabled={
                      props.page === Math.floor(props.reportCount / props.pageSize)
                    }
                    onClick={() => props.setPage(props.page + 1)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      <Grid sx={{ m: "30px" }} />
    </Grid>
  );
}
